import * as React from "react";

import {
  actions,
  PlotPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { ViewportSyncMode } from "@/shared/state/visualization/schema";

import { ZoomMode } from "../messaging/commands";
import { PlotTool } from "../plotTools/PlotTool";

import { PlotPanelContext, Region } from "./PlotPanelContext";

interface PlotPanelProviderProps extends React.PropsWithChildren {
  state: PlotPanelState;
}

export function PlotPanelProvider({ children, state }: PlotPanelProviderProps) {
  const dispatch = useVizDispatch();
  const [activeTool, setActiveTool] = React.useState<PlotTool>(PlotTool.Move);

  // Read viewportSyncMode from config, default to "x" if not set
  const viewportSyncMode = state.config?.viewportSyncMode ?? "x";

  const [isLegendShown, setIsLegendShown] = React.useState(true);
  const [selectedRegion, setSelectedRegion] = React.useState<Region | null>(
    null,
  );
  const [zoomMode, setZoomMode] = React.useState<ZoomMode>("x");

  // Dispatch action to update config when sync mode changes
  const setViewportSyncMode = React.useCallback(
    (mode: ViewportSyncMode) => {
      dispatch(
        actions.setPlotPanelConfig(state.id, { viewportSyncMode: mode }),
      );
    },
    [dispatch, state.id],
  );

  React.useEffect(
    function resetSelectedRegionWhenToolChanges() {
      setSelectedRegion(null);
    },
    [activeTool, setSelectedRegion],
  );

  return (
    <PlotPanelContext.Provider
      value={{
        activeTool,
        setActiveTool,
        viewportSyncMode,
        setViewportSyncMode,
        isLegendShown,
        setIsLegendShown,
        selectedRegion,
        setSelectedRegion,
        zoomMode,
        setZoomMode,
      }}
    >
      {children}
    </PlotPanelContext.Provider>
  );
}
